/**
 * Encoding of the plaintext payload that goes into the ratchet, plus a small
 * amount of padding.
 */
import { fromUtf8, randomBytes, utf8 } from './crypto/primitives.ts';
import type { MessagePayload } from './types.ts';

/**
 * Pad to a multiple of this many bytes.
 *
 * Ciphertext length is visible to the server. Without padding, a 4-byte
 * message and a 400-byte one are trivially distinguishable, which leaks more
 * than people expect (a bare "yes" or "no" is guessable from length alone).
 */
const PAD_BLOCK = 160;

export function encodePayload(payload: MessagePayload): Uint8Array {
  const body = utf8(JSON.stringify(payload));
  // 4-byte big-endian length prefix, then body, then random padding.
  const padded = new Uint8Array(4 + Math.ceil((body.length + 4) / PAD_BLOCK) * PAD_BLOCK - 4);
  new DataView(padded.buffer).setUint32(0, body.length, false);
  padded.set(body, 4);
  const padLen = padded.length - 4 - body.length;
  if (padLen > 0) padded.set(randomBytes(padLen), 4 + body.length);
  return padded;
}

export function decodePayload(bytes: Uint8Array): MessagePayload {
  if (bytes.length < 4) throw new Error('payload too short');
  const length = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength).getUint32(0, false);
  if (length > bytes.length - 4) throw new Error('payload length prefix out of range');
  return JSON.parse(fromUtf8(bytes.subarray(4, 4 + length))) as MessagePayload;
}

/** RFC 4122 v4 id, used for client-side message ids. */
export function newId(): string {
  const b = randomBytes(16);
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const hex = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
